/**
 * One source, expanded: edit metadata, its results, the converted text, the review
 * actions and re-running a step (specs/dataset-workspace.md). What the source profile
 * read from the file (#891): its kind and why, or why it couldn't be read.
 * canApprove is manage on the dataset; a contributor fixes and removes a source only
 * until it is approved (#799).
 */
import React, { useEffect, useState } from 'react';
import { Check, X, Trash2, Pencil, Download, RefreshCw, Database, ScanEye, ExternalLink } from 'lucide-react';
import { Input, Label, Select, Button, Actions, StatusLine } from '../../../components/InstanceSettings/formPrimitives';
import API_ENDPOINTS from '../../../config/api';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { useConfirm } from '../../../components/DialogProvider';
import {
  getSource, updateSource, deleteSource, sourceAction, isSearchLink,
  REVIEWABLE_STATUSES, LOCKED_STATUSES, ACTIVE_STATUSES, SOURCE_KINDS,
} from './datasetApi';
import FixSourcePanel from './FixSourcePanel';
import SourceResults from './SourceResults';
import ConvertedText from './ConvertedText';
import { Detail, FieldRow, Meta, KindChip } from './styles';

export default function SourceDetail({ datasetId, source, canApprove, canContribute, onChanged }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [full, setFull] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ title: '', publisher: '', link: '', source_kind: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [reload, setReload] = useState(0);
  // Reload the markdown when the source moves on (e.g. conversion finished) or is edited.
  useEffect(() => {
    let cancelled = false;
    getSource(datasetId, source.id)
      .then((row) => { if (!cancelled) setFull(row); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [datasetId, source.id, source.status, reload]);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const startEdit = () => {
    setDraft({
      title: source.title || '', publisher: source.publisher || '', link: source.link || '', source_kind: source.source_kind || '',
    });
    setEditing(true);
  };

  const locked = LOCKED_STATUSES.has(source.status);
  const busyInWorker = ACTIVE_STATUSES.has(source.status);
  const indexed = source.status === 'indexed';
  const canFix = canApprove || (canContribute && !locked);
  // Re-download and re-convert send an indexed source back through approval.
  const rerun = async (action) => {
    if (indexed && action !== 'reindex' && !(await confirm({ message: t('datasetWorkspace.rerun.confirmIndexed'), destructive: true }))) return;
    run(() => sourceAction(datasetId, source.id, action));
  };
  const reviewable = REVIEWABLE_STATUSES.has(source.status);
  const needsFix = source.status === 'failed' || source.status === 'awaiting_file';
  const profile = source.metadata?.profile;

  return (
    <Detail>
      {canFix && needsFix && !editing && (
        <FixSourcePanel datasetId={datasetId} source={source} onEdit={startEdit} onChanged={onChanged} />
      )}
      {editing ? (
        <>
          <FieldRow>
            {['title', 'publisher', 'link'].map((field) => (
              <div key={field}>
                <Label htmlFor={`edit-${field}-${source.id}`}>
                  {t(`datasetWorkspace.${field === 'title' ? 'sourceName' : field}`)}
                </Label>
                <Input id={`edit-${field}-${source.id}`} value={draft[field]}
                  onChange={(e) => setDraft({ ...draft, [field]: e.target.value })} />
              </div>
            ))}
            <div>
              <Label htmlFor={`edit-kind-${source.id}`}>{t('datasetWorkspace.kind')}</Label>
              <Select id={`edit-kind-${source.id}`} value={draft.source_kind}
                onChange={(e) => setDraft({ ...draft, source_kind: e.target.value })}>
                <option value="">{t('datasetWorkspace.kinds.none')}</option>
                {SOURCE_KINDS.map((k) => <option key={k} value={k}>{t(`datasetWorkspace.kinds.${k}`)}</option>)}
              </Select>
            </div>
          </FieldRow>
          <Actions>
            <Button $size="small" $variant="primary" disabled={busy}
              onClick={() => run(async () => { await updateSource(datasetId, source.id, draft); setEditing(false); })}>
              {t('datasetWorkspace.save')}
            </Button>
            <Button $size="small" $variant="ghost" onClick={() => setEditing(false)}>{t('datasetWorkspace.cancel')}</Button>
          </Actions>
        </>
      ) : null}
      {editing && indexed && <Meta>{t('datasetWorkspace.rerun.editIndexedHint')}</Meta>}

      {!editing && source.source_kind && (
        <Meta>
          <KindChip $kind={source.source_kind}>{t(`datasetWorkspace.kinds.${source.source_kind}`)}</KindChip>
          {profile?.kind === source.source_kind && profile?.kindReason}
        </Meta>
      )}
      {!editing && isSearchLink(source) && <Meta>{t('datasetWorkspace.searchLinkHelp')}</Meta>}
      {profile?.error && <Meta style={{ color: '#fbbf24' }}>{t('datasetWorkspace.profileFailed', { error: profile.error })}</Meta>}

      <SourceResults source={source} />

      <ConvertedText datasetId={datasetId} source={source} markdown={full?.converted_markdown} canEdit={canFix}
        onSaved={() => { setReload((n) => n + 1); onChanged(); }} />

      <Actions>
        {canApprove && reviewable && (
          <>
            <Button $size="small" $variant="primary" disabled={busy}
              onClick={() => run(() => sourceAction(datasetId, source.id, 'approve'))}>
              <Check /> {t('datasetWorkspace.approve')}
            </Button>
            <Button $size="small" disabled={busy}
              onClick={() => run(() => sourceAction(datasetId, source.id, 'reject'))}>
              <X /> {t('datasetWorkspace.reject')}
            </Button>
          </>
        )}
        {canFix && !busyInWorker && !editing && !needsFix && (
          <Button $size="small" $variant="ghost" onClick={startEdit}><Pencil /> {t('datasetWorkspace.edit')}</Button>
        )}
        {canFix && !busyInWorker && source.link && !isSearchLink(source) && (
          <Button $size="small" $variant="ghost" disabled={busy} onClick={() => rerun('redownload')}>
            <Download /> {t('datasetWorkspace.rerun.redownload')}
          </Button>
        )}
        {canFix && !busyInWorker && !needsFix && (source.has_file || source.source_url) && (
          <Button $size="small" $variant="ghost" disabled={busy} onClick={() => rerun('reconvert')}>
            <RefreshCw /> {t('datasetWorkspace.rerun.reconvert')}
          </Button>
        )}
        {source.has_file && (
          <Button $size="small" $variant="ghost"
            onClick={() => window.open(API_ENDPOINTS.ADMIN_DATASET_SOURCE_FILE(datasetId, source.id), '_blank', 'noopener')}>
            <ExternalLink /> {t('datasetWorkspace.openPdf')}
          </Button>
        )}
        {canApprove && !busyInWorker && source.has_file && source.vision_estimate_usd != null && (
          <Button $size="small" $variant="ghost" disabled={busy}
            onClick={async () => {
              if (await confirm(t('datasetWorkspace.vision.confirm', {
                pages: source.metadata?.convertMeta?.visualPages?.length || 0, cost: source.vision_estimate_usd,
              }))) run(() => sourceAction(datasetId, source.id, 'vision'));
            }}>
            <ScanEye /> {t('datasetWorkspace.vision.run', {
              pages: source.metadata?.convertMeta?.visualPages?.length || 0, cost: source.vision_estimate_usd,
            })}
          </Button>
        )}
        {canApprove && (indexed || (source.status === 'failed' && full?.converted_markdown)) && (
          <Button $size="small" $variant="ghost" disabled={busy} onClick={() => rerun('reindex')}>
            <Database /> {t('datasetWorkspace.rerun.reindex')}
          </Button>
        )}
        {canContribute && !locked && (
          <Button $size="small" $variant="ghost" $tone="danger" disabled={busy}
            onClick={async () => {
              if (await confirm({ message: t('datasetWorkspace.confirmRemove'), confirmLabel: t('datasetWorkspace.remove'), destructive: true })) run(() => deleteSource(datasetId, source.id));
            }}>
            <Trash2 /> {t('datasetWorkspace.remove')}
          </Button>
        )}
        {error && <StatusLine $error>{error}</StatusLine>}
      </Actions>
    </Detail>
  );
}
